import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import { RadioCardItem, RadioCardLabel, RadioCardRoot } from '@/components/ui/radio-card';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/auth-context';
import { usePreviewBrandName } from './use-preview-brand-name';
import { useIsLocalDiscountMode } from '@/hooks/use-is-local-discount-mode';
import { useMigrateProgramDiscountCodes } from '@/hooks/actions/use-migrate-program-discount-codes';
import { useUpdateProgram } from '@/hooks/actions/use-update-program';
import { useDiscountCodeMigrationPreview } from '@/hooks/queries/use-discount-code-migration-preview';
import { hasCodePatternChanged, normalizeCodePattern } from './code-pattern-change';
import ApplyFormatToExistingCodesDialog from './ApplyFormatToExistingCodesDialog';
import { Box, Flex, HStack, Input, Separator, Text, VStack } from '@chakra-ui/react';
import {
  CodePatternPart,
  CodePatternSeparator,
  DEFAULT_CODE_PATTERN,
  DiscountCodeTypes,
  type CodePattern,
} from '@fluenceur/types';
import DiscountCodePatternBuilder from './DiscountCodePatternBuilder';
import { ShopifyOnlyDiscountFields } from './ShopifyOnlyDiscountFields';
import { zodResolver } from '@hookform/resolvers/zod';

import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DollarCircle, PercentageCircle } from 'iconoir-react';

// Validation schema
const discountCodeSchema = (t: (key: string) => string) =>
  z
    .object({
      applyDiscount: z.boolean(),
      discountType: z
        .enum([DiscountCodeTypes.Percentage, DiscountCodeTypes.FixedAmount])
        .nullable()
        .optional(),
      discountValue: z.number().nullable().optional(),
      minPurchaseAmount: z.number().nullable().optional(),
      autoApplyDiscount: z.boolean().optional(),
      oncePerCustomer: z.boolean().optional(),
      codePattern: z
        .object({
          parts: z
            .array(
              z.object({
                type: z.nativeEnum(CodePatternPart),
                value: z.string().max(20).optional(),
              })
            )
            .min(1)
            .max(5),
          separator: z.nativeEnum(CodePatternSeparator),
          uppercase: z.boolean(),
        })
        .nullable()
        .optional(),
    })
    .superRefine((input, ctx) => {
      if (input.applyDiscount) {
        if (!input.discountType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['discountType'],
            message: t('programs.validation.discountTypeRequired'),
          });
        }
        if (!input.discountValue || input.discountValue <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['discountValue'],
            message: t('programs.validation.discountValuePositive'),
          });
        }
        if (
          input.discountType === DiscountCodeTypes.Percentage &&
          input.discountValue &&
          input.discountValue > 100
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['discountValue'],
            message: t('programs.validation.discountPercentageMax'),
          });
        }
        // Validate minPurchaseAmount if provided
        if (input.minPurchaseAmount !== null && input.minPurchaseAmount !== undefined) {
          if (input.minPurchaseAmount < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['minPurchaseAmount'],
              message: t('programs.validation.minPurchaseAmountPositive'),
            });
          }
          if (input.minPurchaseAmount > 999999.99) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['minPurchaseAmount'],
              message: t('programs.validation.minPurchaseAmountMax'),
            });
          }
        }
      }
    });

export type ProgramDiscountCodeFormData = {
  applyDiscount: boolean;
  discountType?: DiscountCodeTypes | null;
  discountValue?: number | null;
  minPurchaseAmount?: number | null;
  autoApplyDiscount?: boolean;
  oncePerCustomer?: boolean;
  codePattern?: CodePattern | null;
};

interface ProgramDiscountCodeFormProps {
  programId: string;
  defaultValues?: Partial<ProgramDiscountCodeFormData>;
  onSuccess: () => void;
  onSubmitting: (isSubmitting: boolean) => void;
}

const ProgramDiscountCodeForm = forwardRef(
  ({ programId, defaultValues, onSuccess, onSubmitting }: ProgramDiscountCodeFormProps, ref) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const previewBrandName = usePreviewBrandName();
    const queryClient = useQueryClient();
    const schema = useMemo(() => discountCodeSchema(t), [t]);

    // Get currency from user's current brand, default to EUR
    const currency = user?.currentBrand?.currency || 'EUR';

    // Brands on local discount codes (internal/partner) have no Shopify
    // discount object to carry these settings - hide them rather than
    // showing controls that do nothing.
    const isLocalDiscountMode = useIsLocalDiscountMode();

    const discountOptions = [
      {
        value: DiscountCodeTypes.Percentage,
        title: t('programs.modals.editDiscountCode.percentage'),
        icon: <PercentageCircle width={20} height={20} />,
        description: t('programs.modals.editDiscountCode.percentageDescription'),
      },
      {
        value: DiscountCodeTypes.FixedAmount,
        title: t('programs.modals.editDiscountCode.fixedAmount'),
        icon: <DollarCircle width={20} height={20} />,
        description: t('programs.modals.editDiscountCode.fixedAmountDescription'),
      },
    ];

    const methods = useForm<ProgramDiscountCodeFormData>({
      resolver: zodResolver(schema),
      defaultValues,
    });

    const { register, setValue, formState, control } = methods;
    const applyDiscount =
      useWatch({ control, name: 'applyDiscount' }) ?? defaultValues?.applyDiscount ?? false;
    const watchedCodePattern = useWatch({ control, name: 'codePattern' });
    const watchedDiscountValue = useWatch({ control, name: 'discountValue' });

    const customCodeFormat = watchedCodePattern !== null && watchedCodePattern !== undefined;

    // The format the brand is about to save, compared to the one on file. Only
    // a move asks a question: saving the same format again changes no code.
    const patternChanged = hasCodePatternChanged(defaultValues?.codePattern, watchedCodePattern);
    const candidatePattern = normalizeCodePattern(watchedCodePattern);
    const { data: migrationPreview, refetch: refetchMigrationPreview } =
      useDiscountCodeMigrationPreview(programId, candidatePattern, { enabled: patternChanged });

    const updateProgramMutation = useUpdateProgram<ProgramDiscountCodeFormData>();
    const migrateMutation = useMigrateProgramDiscountCodes();
    const [pendingSave, setPendingSave] = useState<ProgramDiscountCodeFormData | null>(null);
    // The click starts the work before any mutation does: a changed format
    // waits on the preview first. That wait belongs to the button, otherwise it
    // stays idle and clickable while a request is in flight.
    const [isResolvingPreview, setIsResolvingPreview] = useState(false);

    useEffect(() => {
      onSubmitting(
        isResolvingPreview || updateProgramMutation.isPending || migrateMutation.isPending
      );
    }, [
      onSubmitting,
      isResolvingPreview,
      updateProgramMutation.isPending,
      migrateMutation.isPending,
    ]);

    // The backend neutralizes Shopify-only settings on every write for
    // local-mode brands (sanitizeDiscountSettingsForMode), so the form sends
    // its data as-is and the API stays the single source of truth.
    //
    // Saving a format never migrates by itself: the migration is a second,
    // explicit call, and a failed one leaves the format saved.
    const save = async (data: ProgramDiscountCodeFormData, applyToExisting: boolean) => {
      try {
        await updateProgramMutation.mutateAsync({ programId, payload: data });
      } catch (error) {
        console.error('Failed to update discount details:', error);
        return;
      }

      // Invalidate specific program and programs list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      queryClient.invalidateQueries({ queryKey: ['programs'] });

      if (applyToExisting) {
        try {
          await migrateMutation.mutateAsync({
            programId,
            codePattern: normalizeCodePattern(data.codePattern),
          });
        } catch {
          // The refusal is already on screen through the hook's toaster, and
          // the format the brand chose stays saved.
        }
      }

      onSuccess();
    };

    // A save clicked before the preview answered used to read `undefined` and
    // conclude there was nothing to ask about, so a fast click silently skipped
    // the question. The answer is waited for instead; a preview that cannot be
    // read saves the format without migrating, which is what the brand asked
    // for in the first place.
    const onSubmit = async (data: ProgramDiscountCodeFormData) => {
      if (!patternChanged) {
        void save(data, false);
        return;
      }

      let preview = migrationPreview;

      if (!preview) {
        setIsResolvingPreview(true);
        try {
          const result = await refetchMigrationPreview();
          if (result.error) throw result.error;
          preview = result.data;
        } catch (error) {
          console.error('Failed to read the discount code migration preview:', error);
          void save(data, false);
          return;
        } finally {
          // Whatever the answer, the button stops working here: either the save
          // takes over and reports its own pending state, or the dialog opens
          // and becomes the surface the brand is acting on.
          setIsResolvingPreview(false);
        }
      }

      if (preview?.hasGeneratedCodes) {
        setPendingSave(data);
        return;
      }

      void save(data, false);
    };

    // Expose the submit method to the parent using `ref`
    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit(onSubmit),
    }));

    const handleDialogConfirm = (applyToExisting: boolean) => {
      const data = pendingSave;
      setPendingSave(null);
      if (!data) return;
      void save(data, applyToExisting && !migrationPreview?.running);
    };

    const handleCodePatternChange = (pattern: CodePattern) => {
      setValue('codePattern', pattern, { shouldValidate: true });
    };

    const handleCustomCodeFormatToggle = (checked: boolean) => {
      if (checked) {
        setValue('codePattern', DEFAULT_CODE_PATTERN, { shouldValidate: true });
      } else {
        setValue('codePattern', null, { shouldValidate: true });
      }
    };

    const [discountType, setDiscountType] = useState<DiscountCodeTypes | null>(
      defaultValues?.discountType ?? DiscountCodeTypes.Percentage
    );

    useEffect(() => {
      if (applyDiscount) {
        setValue('discountType', discountType);
        setValue('discountValue', defaultValues?.discountValue ?? 10);
      } else {
        setValue('discountType', null);
        setValue('discountValue', null);
      }
    }, [applyDiscount, discountType, defaultValues?.discountValue, setValue]);

    const handleTypeChange = (value: string) => {
      setDiscountType(value as DiscountCodeTypes);
      setValue('discountType', value as DiscountCodeTypes, { shouldValidate: true });
    };

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <VStack gap={6} align="stretch">
            {/* Apply Discount Toggle */}
            <Box>
              <Switch
                colorPalette="primary"
                checked={applyDiscount}
                onCheckedChange={(e) => setValue('applyDiscount', e.checked)}
              >
                {t('programs.modals.editDiscountCode.applyDiscount')}
              </Switch>
              <Text fontSize="sm" color="gray.500" mt={2}>
                {t('programs.modals.editDiscountCode.applyDiscountDescription')}
              </Text>
            </Box>

            {applyDiscount && (
              <Box>
                {/* Discount Type Selection */}
                <RadioCardRoot
                  colorPalette="primary"
                  value={discountType || ''}
                  onValueChange={(value) => handleTypeChange(value.value ?? '')}
                >
                  <RadioCardLabel>
                    {t('programs.modals.editDiscountCode.discountType')}
                  </RadioCardLabel>
                  <HStack align="stretch" w="full">
                    {discountOptions.map((option, index) => (
                      <RadioCardItem
                        key={index}
                        value={option.value}
                        label={
                          <Flex align="center">
                            {option.icon}
                            <Text ml={2}>{option.title}</Text>
                          </Flex>
                        }
                        description={option.description}
                      />
                    ))}
                  </HStack>
                </RadioCardRoot>

                <VStack gap={4} mt={6} align="stretch">
                  <Field
                    label={t('programs.modals.editDiscountCode.discountValue')}
                    invalid={!!formState.errors.discountValue}
                    errorText={formState.errors.discountValue?.message as string}
                  >
                    <InputGroup
                      w="full"
                      colorPalette="primary"
                      endElement={
                        <Text fontWeight="bold">
                          {discountType === DiscountCodeTypes.Percentage ? '%' : currency}
                        </Text>
                      }
                    >
                      <Input
                        type="number"
                        step="1"
                        placeholder={t('programs.modals.editDiscountCode.discountValuePlaceholder')}
                        {...register('discountValue', { valueAsNumber: true })}
                      />
                    </InputGroup>
                  </Field>

                  {!isLocalDiscountMode && (
                    <ShopifyOnlyDiscountFields currency={currency} defaultValues={defaultValues} />
                  )}

                  {/* Code Format Pattern */}
                  <Separator />
                  <Box>
                    <Switch
                      colorPalette="primary"
                      checked={customCodeFormat}
                      onCheckedChange={(e) => handleCustomCodeFormatToggle(e.checked)}
                    >
                      {t('programs.modals.editDiscountCode.customCodeFormat')}
                    </Switch>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      {t('programs.modals.editDiscountCode.customCodeFormatDescription')}
                    </Text>
                  </Box>
                  {customCodeFormat && (
                    <DiscountCodePatternBuilder
                      value={watchedCodePattern ?? DEFAULT_CODE_PATTERN}
                      onChange={handleCodePatternChange}
                      brandName={previewBrandName}
                      discountValue={watchedDiscountValue ?? 10}
                    />
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        </form>

        {pendingSave !== null && migrationPreview && (
          <ApplyFormatToExistingCodesDialog
            open
            preview={{
              renamed: migrationPreview.renamed,
              kept: migrationPreview.kept,
              transitionDays: migrationPreview.transitionDays,
              running: migrationPreview.running,
            }}
            onConfirm={handleDialogConfirm}
            onCancel={() => setPendingSave(null)}
          />
        )}
      </FormProvider>
    );
  }
);

export default ProgramDiscountCodeForm;
